import { useEditLogin } from "@/api/mutations/logins";
import type { LoginItemType } from "@/api/queries/logins/types";
import { CountrySelect } from "@/components/country-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Message } from "@/components/ui/message";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { Info, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

const formSchema = z.object({
  location: z.string().optional(),
  city: z.string().optional(),
  password: z.string().optional(),
  totpSecretKey: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface EditLoginDialogProps {
  loginItem: LoginItemType;
}

export const EditLoginDialog = ({ loginItem }: EditLoginDialogProps) => {
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useEditLogin(loginItem.id);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      location: loginItem.location,
      city: loginItem.city,
      password: "",
      totpSecretKey: "",
    },
  });

  useEffect(() => {
    reset({
      location: loginItem.location,
      city: loginItem.city,
      password: "",
      totpSecretKey: "",
    });
  }, [loginItem]);

  const onSubmit = async (data: FormData) => {
    mutateAsync(data)
      .then(() => {
        toast.success("User created");
        setOpen(false);
        reset();
      })
      .catch((err: AxiosError<{ message: string }, unknown>) => {
        toast.error(err.response?.data?.["message"] ?? "Failed to create user");
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button data-testid="edit-login-trigger-btn" className="w-full justify-start gap-2">
          <Pencil className="size-4" />
          Edit Login
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Login</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Location</Label>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <CountrySelect
                  placeholder="Please select location"
                  searchPlaceholder="Search location"
                  empyPlaceholder="No Location found."
                  value={field.value}
                  onSelect={(value) => {
                    field.onChange({ target: { value } });
                  }}
                />
              )}
            />
            {errors.location && <Message variant="error">{errors.location.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>City</Label>
            <Controller
              name="city"
              control={control}
              render={({ field }) => <Input {...field} placeholder="City" />}
            />
            {errors.city && <Message variant="error">{errors.city.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Password</Label>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        You can leave the password input empty, and the password will remain the
                        same
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Input {...field} type="password" placeholder="Password" />
                </div>
              )}
            />
            {errors.password && <Message variant="error">{errors.password.message}</Message>}
          </div>

          <div className="space-y-2">
            <Label>Secret Key</Label>
            <Controller
              name="totpSecretKey"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        You can leave the "TOTP Secret Key" input empty, and the "TOTP Secret Key"
                        will remain the same
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Input {...field} placeholder="TOTP Secret Key" />
                </div>
              )}
            />
            {errors.totpSecretKey && (
              <Message variant="error">{errors.totpSecretKey.message}</Message>
            )}
          </div>

          <Button className="w-full" type="submit" disabled={isPending}>
            {isPending ? "Editing..." : "Edit"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
